import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  IconChevronRight,
  IconCheck,
  IconClose,
  IconHelp,
  IconHouse,
  IconUserSilhouette,
} from "./icons";
import {
  PRACTICE_ACCENTS,
  type Practice,
  type PracticeAccent,
} from "./practices";
import { usePractice } from "./practice.provider";

const DRAG_DISMISS = 120;

export function CabinetSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { practice, practices, setPractice } = usePractice();
  const [drag, setDrag] = useState(0);
  const startY = useRef<number | null>(null);
  const dragRef = useRef(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setDrag(0);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  if (!open || typeof document === "undefined") return null;

  const choose = (uuid: string) => {
    setPractice(uuid);
    onClose();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.target as HTMLElement;
    const inScroller = scrollerRef.current?.contains(t);
    if (inScroller && (scrollerRef.current?.scrollTop ?? 0) > 0) return;
    startY.current = e.touches[0]?.clientY ?? null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null) return;
    const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
    if (dy > 0) setDrag(dy);
    else setDrag(0);
  };
  const onTouchEnd = () => {
    if (startY.current === null) return;
    startY.current = null;
    if (dragRef.current > DRAG_DISMISS) onClose();
    setDrag(0);
  };

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        style={{
          opacity: Math.max(0.2, 1 - drag / 300),
          transition:
            drag === 0
              ? "opacity 240ms cubic-bezier(0.32, 0.72, 0, 1)"
              : "none",
        }}
      />
      <div
        role="dialog"
        aria-label="Changer de cabinet"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          transform: `translate3d(0, ${drag}px, 0)`,
          transition:
            drag === 0
              ? "transform 320ms cubic-bezier(0.32, 0.72, 0, 1)"
              : "none",
        }}
        className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-[28px] bg-[var(--surface-1)] pb-[max(env(safe-area-inset-bottom),1rem)] shadow-2xl ring-1 ring-[var(--border-1)] sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl sm:pb-4"
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-4 sm:px-6">
          <h2 className="min-w-0 flex-1 text-[20px] font-bold tracking-tight text-[var(--text-1)]">
            Profil
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--surface-2)] text-[var(--text-1)] ring-1 ring-[var(--border-1)] transition active:scale-95"
          >
            <IconClose width={20} height={20} strokeWidth={2.5} />
          </button>
        </div>

        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto overscroll-contain px-4 pb-4 sm:px-6"
        >
          <UserHeader />

          <SettingsSection title="Cabinet">
            <ul className="overflow-hidden rounded-2xl bg-[var(--surface-2)]/50 ring-1 ring-[var(--border-1)]">
              {practices.map((p: Practice, i) => {
                const active = p.uuid === practice.uuid;
                return (
                  <li
                    key={p.uuid}
                    className={i > 0 ? "border-t border-[var(--border-1)]" : ""}
                  >
                    <button
                      type="button"
                      onClick={() => choose(p.uuid)}
                      className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition active:bg-[var(--surface-3)]"
                    >
                      <AccentDot accent={p.accent} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-semibold text-[var(--text-1)]">
                          {p.name}
                        </p>
                        <p className="truncate text-[12.5px] text-[var(--text-3)]">
                          {p.city}
                        </p>
                      </div>
                      {active ? (
                        <span
                          className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-white"
                          style={{
                            backgroundColor: PRACTICE_ACCENTS[p.accent].primary,
                          }}
                        >
                          <IconCheck width={16} height={16} strokeWidth={2.5} />
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </SettingsSection>

          <SettingsSection
            title="Aide"
            icon={<IconHelp width={16} height={16} />}
          >
            <ul className="overflow-hidden rounded-2xl bg-[var(--surface-2)]/50 ring-1 ring-[var(--border-1)]">
              <SettingsRow
                icon={<IconHelp width={18} height={18} />}
                label="Centre d'aide"
                description="FAQ, contact, conditions générales"
              />
              <SettingsRow
                icon={<IconHouse width={18} height={18} />}
                label="À propos de Ligam"
                description="Version 1.0 · Mentions légales"
              />
            </ul>
          </SettingsSection>

          <SettingsSection title="Compte">
            <ul className="overflow-hidden rounded-2xl bg-[var(--surface-2)]/50 ring-1 ring-[var(--border-1)]">
              <SettingsRow
                icon={<IconUserSilhouette width={18} height={18} />}
                label="Modifier le profil"
                description="Nom, email, mot de passe"
              />
              <SettingsRow label="Se déconnecter" danger hideChevron />
            </ul>
          </SettingsSection>
        </div>
      </div>
    </>,
    document.body,
  );
}

function AccentDot({ accent }: { accent: PracticeAccent }) {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 shrink-0 rounded-full ring-2 ring-[var(--surface-1)]"
      style={{ backgroundColor: PRACTICE_ACCENTS[accent].primary }}
    />
  );
}

function UserHeader() {
  return (
    <div className="mb-5 flex items-center gap-3 rounded-3xl bg-[var(--surface-2)]/50 p-4 ring-1 ring-[var(--border-1)]">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary-100)] text-[15px] font-bold text-[var(--sf-primary)]">
        LM
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[16px] font-bold tracking-tight text-[var(--text-1)]">
          Laure Martin
        </p>
        <p className="truncate text-[12.5px] text-[var(--text-3)]">
          Maman · laure.martin@example.fr
        </p>
      </div>
    </div>
  );
}

function SettingsSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 flex items-center gap-1.5 px-1 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--text-3)]">
        {icon ? <span aria-hidden>{icon}</span> : null}
        {title}
      </h3>
      {children}
    </section>
  );
}

function SettingsRow({
  icon,
  label,
  description,
  danger,
  hideChevron,
}: {
  icon?: ReactNode;
  label: string;
  description?: string;
  danger?: boolean;
  hideChevron?: boolean;
}) {
  return (
    <li className="border-t border-[var(--border-1)] first:border-t-0">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition active:bg-[var(--surface-3)]"
      >
        {icon ? (
          <span
            className={`inline-flex size-9 shrink-0 items-center justify-center rounded-2xl ${
              danger
                ? "bg-[var(--sf-primary-100)] text-[var(--sf-primary)]"
                : "bg-[var(--surface-1)] text-[var(--text-2)] ring-1 ring-[var(--border-1)]"
            }`}
          >
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <p
            className={`text-[14.5px] font-semibold ${
              danger ? "text-[var(--sf-primary)]" : "text-[var(--text-1)]"
            }`}
          >
            {label}
          </p>
          {description ? (
            <p className="mt-0.5 truncate text-[12px] text-[var(--text-3)]">
              {description}
            </p>
          ) : null}
        </div>
        {!hideChevron ? (
          <IconChevronRight
            width={14}
            height={14}
            className="text-[var(--text-3)]"
          />
        ) : null}
      </button>
    </li>
  );
}
